import { useState } from "react";

import { useUnitsOfMeasure } from "@/shared/hooks/useUnitsOfMeasure";
import { useNotification } from "@/shared/hooks/useNotification";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field, FieldLabel } from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import type { SowingInstruction } from "../../services/sowingInstructionsApi";
import { useMarkOvergrown, useRecordReadyForDispatch } from "../../hooks/useSowingInstructions";
import { useConfirm } from "@/shared/contexts/confirm-context";
import { SowingConfirmBody } from "./SowingConfirmSummary";

// Legacy: older per-seedling records may carry unit=2 (Grams) due to a prior frontend mismatch.
const PRICING_MODE_PER_SEEDLING = 2;
const UNIT_GRAMS = 2;
const UNIT_PIECES = 5;

function resolveUnit(instruction: SowingInstruction): number {
  if (instruction.pricingMode === PRICING_MODE_PER_SEEDLING && instruction.unit === UNIT_GRAMS) {
    return UNIT_PIECES;
  }
  return instruction.unit;
}

interface RecordDispatchDialogProps {
  instruction: SowingInstruction;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

type DispatchAction = "ready" | "overgrown";

export const RecordDispatchDialog = ({
  instruction,
  open,
  onOpenChange,
  onSuccess,
}: RecordDispatchDialogProps) => {
  const maxQuantity = instruction.quantityGrowing ?? null;

  const [action, setAction] = useState<DispatchAction>("ready");
  const [formData, setFormData] = useState({
    actualDate: new Date().toISOString().split("T")[0],
    quantity: maxQuantity != null ? maxQuantity.toString() : "",
    unitOfMeasure: resolveUnit(instruction).toString(),
    reason: "",
    notes: "",
  });

  const readyMutation = useRecordReadyForDispatch();
  const overgrownMutation = useMarkOvergrown();
  const notify = useNotification();
  const unitsQuery = useUnitsOfMeasure();
  const confirm = useConfirm();

  const resolvedUnit = resolveUnit(instruction);
  const unitName = unitsQuery.data?.find((u) => u.value === resolvedUnit)?.name?.toLowerCase() ?? "units";

  const isPending =
    readyMutation.isPending ||
    overgrownMutation.isPending;

  const quantityValue = parseFloat(formData.quantity);
  const isOverMax = maxQuantity != null && !isNaN(quantityValue) && quantityValue > maxQuantity;

  const handleSubmit = () => {
    if (!formData.actualDate || !formData.quantity) {
      notify.error("Please fill in all required fields");
      return;
    }

    if (action === "ready" && isOverMax) {
      notify.error(`Ready quantity cannot exceed growing quantity (${maxQuantity} ${unitName})`);
      return;
    }

    if (action === "overgrown" && !formData.reason) {
      notify.error("Please provide a reason for overgrown status");
      return;
    }

    const isOvergrown = action === "overgrown";
    confirm({
      title: isOvergrown ? "Mark as overgrown?" : "Mark ready for dispatch?",
      description: isOvergrown
        ? "This quantity will be marked as overgrown."
        : "This quantity will be marked ready for dispatch.",
      variant: isOvergrown ? "destructive" : "default",
      confirmLabel: isOvergrown ? "Mark overgrown" : "Mark ready",
      body: SowingConfirmBody(instruction, [
        { label: "Date", value: formData.actualDate },
        {
          label: isOvergrown ? "Quantity overgrown" : "Quantity ready",
          value: `${formData.quantity} ${unitName}`,
        },
        ...(isOvergrown && formData.reason
          ? [{ label: "Reason", value: formData.reason }]
          : []),
      ]),
      onConfirm: () => {
        if (action === "ready") {
          return readyMutation
            .mutateAsync({
              sowingInstructionId: instruction.id,
              actualReadyDate: new Date(formData.actualDate).toISOString(),
              quantityReady: parseFloat(formData.quantity),
              quantityReadyUnitOfMeasure: parseInt(formData.unitOfMeasure),
              notes: formData.notes,
            })
            .then(() => {
              notify.success("Marked as ready for dispatch successfully");
              onSuccess();
            })
            .catch((error: any) => {
              notify.error(
                error.response?.data?.message ||
                  "Failed to record ready for dispatch"
              );
              throw error;
            });
        }
        return overgrownMutation
          .mutateAsync({
            sowingInstructionId: instruction.id,
            reason: formData.reason,
            quantityOvergrown: formData.quantity
              ? parseFloat(formData.quantity)
              : undefined,
            notes: formData.notes,
          })
          .then(() => {
            notify.success("Instruction marked as overgrown");
            onSuccess();
          })
          .catch((error: any) => {
            notify.error(
              error.response?.data?.message || "Failed to mark as overgrown"
            );
            throw error;
          });
      },
    });
  };

  const trayTypeLabel =
    instruction.pricingMode === PRICING_MODE_PER_SEEDLING
      ? "Per Seedling/Piece"
      : instruction.trayTypeName ||
        (instruction.trayType === 210
          ? "210-Hole Tray"
          : instruction.trayType === 442
            ? "442-Hole Tray"
            : null);
  const fmtDate = (d?: string | null) => {
    if (!d) return null;
    const dt = new Date(d);
    return dt.getFullYear() > 1 ? dt.toLocaleDateString() : null;
  };
  const sownDate = fmtDate(instruction.actualSowingDate) ?? fmtDate(instruction.scheduledSowingDate);
  const germinationDate = fmtDate(instruction.germinationStartedDate);
  const locationLabel = (() => {
    const gh = instruction.greenhouseName?.trim();
    const bed = instruction.bedNumber?.trim();
    if (gh && bed) return `${gh} · ${bed}`;
    return gh || bed || null;
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Record Ready for Dispatch</DialogTitle>
          <DialogDescription>
            {instruction.instructionNumber} — {instruction.quantityToSow} {unitName}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 rounded-md border bg-muted/40 px-3 py-2 text-sm">
            {instruction.cropTypeName && (
              <div>
                <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Crop Type</p>
                <p className="font-medium">{instruction.cropTypeName}</p>
              </div>
            )}
            {instruction.variety && (
              <div>
                <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Variety</p>
                <p className="font-medium">{instruction.variety}</p>
              </div>
            )}
            {trayTypeLabel && (
              <div>
                <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Tray Type</p>
                <p className="font-medium">{trayTypeLabel}</p>
              </div>
            )}
            {locationLabel && (
              <div>
                <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Location</p>
                <p className="font-medium">{locationLabel}</p>
              </div>
            )}
            {sownDate && (
              <div>
                <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  {instruction.actualSowingDate ? "Date Sown" : "Scheduled Sowing"}
                </p>
                <p className="font-medium">{sownDate}</p>
              </div>
            )}
            {germinationDate && (
              <div>
                <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Germinated</p>
                <p className="font-medium">{germinationDate}</p>
              </div>
            )}
            {typeof instruction.quantityGrowing === "number" && instruction.quantityGrowing > 0 && (
              <div>
                <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Growing Qty</p>
                <p className="font-medium">{instruction.quantityGrowing}</p>
              </div>
            )}
          </div>

          <Field>
            <FieldLabel>Action <span className="text-destructive">*</span></FieldLabel>
            <div className="flex gap-2">
              <Button
                variant={action === "ready" ? "default" : "outline"}
                onClick={() => setAction("ready")}
                disabled={isPending}
                className="flex-1"
              >
                Ready for Dispatch
              </Button>
              <Button
                variant={action === "overgrown" ? "destructive" : "outline"}
                onClick={() => setAction("overgrown")}
                disabled={isPending}
                className="flex-1"
              >
                Overgrown
              </Button>
            </div>
          </Field>

          {action === "overgrown" && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-3">
              <p className="text-sm text-red-800">
                Marking as overgrown will terminate this instruction.
              </p>
            </div>
          )}

          <Field>
            <FieldLabel htmlFor="actualDate">
              Date <span className="text-destructive">*</span>
            </FieldLabel>
            <DatePicker
              id="actualDate"
              value={formData.actualDate}
              onChange={(value) =>
                setFormData((prev) => ({ ...prev, actualDate: value }))
              }
              disabled={isPending}
            />
          </Field>

          {action !== "overgrown" && (
            <>
              <Field>
                <FieldLabel htmlFor="quantity">
                  Quantity <span className="text-destructive">*</span>
                </FieldLabel>
                <Input
                  id="quantity"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={formData.quantity}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      quantity: e.target.value,
                    }))
                  }
                  disabled={isPending}
                  className={isOverMax ? "border-destructive" : undefined}
                />
                {maxQuantity != null && (
                  <p className={`text-xs mt-1 ${isOverMax ? "text-destructive font-medium" : "text-muted-foreground"}`}>
                    Maximum allowed: {maxQuantity} {unitName} (from growing stage)
                    {isOverMax && ` — entered ${quantityValue - maxQuantity} too many`}
                  </p>
                )}
              </Field>

              <Field>
                <FieldLabel htmlFor="unit">Unit</FieldLabel>
                <Select
                  value={formData.unitOfMeasure}
                  onValueChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      unitOfMeasure: value,
                    }))
                  }
                  disabled={isPending}
                >
                  <SelectTrigger id="unit">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {unitsQuery.data?.map((unit) => (
                      <SelectItem key={unit.value} value={unit.value.toString()}>
                        {unit.name} ({unit.abbreviation})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </>
          )}

          {action === "overgrown" && (
            <Field>
              <FieldLabel htmlFor="reason">
                Reason <span className="text-destructive">*</span>
              </FieldLabel>
              <Input
                id="reason"
                placeholder="Why are plants overgrown?"
                value={formData.reason}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    reason: e.target.value,
                  }))
                }
                disabled={isPending}
              />
            </Field>
          )}

          <Field>
            <FieldLabel htmlFor="notes">Notes</FieldLabel>
            <Textarea
              id="notes"
              placeholder="Any additional notes..."
              value={formData.notes}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, notes: e.target.value }))
              }
              disabled={isPending}
            />
          </Field>

          <div className="flex gap-2 justify-end">
            <Button
              variant="secondary"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isPending || (action === "ready" && !formData.quantity)}
              variant={action === "overgrown" ? "destructive" : "default"}
            >
              {isPending ? "Recording..." : "Record"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};